

import { useState } from "react";
import { clientsApi } from "@/lib/api";

interface AddClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClientCreated: (clientId: number, clientName: string) => void;
}

export default function AddClientModal({ isOpen, onClose, onClientCreated }: AddClientModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = "Client name is required";
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      const response = await clientsApi.create({
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
      });

      const newClient = response.data?.data || response.data;
      const clientId = newClient.id;
      const clientName = newClient.name;

      onClientCreated(clientId, clientName);
      setFormData({ name: "", email: "", phone: "" });
      onClose();
    } catch (error) {
      setErrors({ submit: "Failed to create client" });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/60 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-stone-800">Add New Client</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-stone-100 text-stone-500 hover:bg-stone-200 flex items-center justify-center"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Client Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter client name"
              className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors outline-none ${
                errors.name
                  ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
                  : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
              }`}
            />
            {errors.name && <p className="text-xs text-rose-500 mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="Enter email (optional)"
              className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors outline-none ${
                errors.email
                  ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
                  : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
              }`}
            />
            {errors.email && <p className="text-xs text-rose-500 mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="Enter phone (optional)"
              className="w-full px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-colors outline-none"
            />
          </div>

          {errors.submit && <p className="text-xs text-rose-500">{errors.submit}</p>}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-lg border border-stone-200 text-stone-700 font-medium hover:bg-stone-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 rounded-lg gold-gradient text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {loading ? "Creating..." : "Create Client"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
